import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FileCheck, Type, Network, FileText, BookOpen, Eye, LifeBuoy, ShieldCheck } from 'lucide-react';
import { DjayMascot } from './DjayMascot';
/**
 * Pipeline — onze 8 kwaliteitschecks, in plain Dutch.
 * Animeert volgordelijk door alle 8 stappen.
 */
const CHECKS = [{
  n: 1,
  icon: FileCheck,
  label: 'Vorm',
  desc: 'Klopt de opbouw?',
  color: 'sky'
}, {
  n: 2,
  icon: Type,
  label: 'Vraagtype',
  desc: 'Is dit het juiste type voor dit doel?',
  color: 'indigo'
}, {
  n: 3,
  icon: Network,
  label: 'Leerkaart',
  desc: 'Past de vraag in de routekaart?',
  color: 'violet'
}, {
  n: 4,
  icon: FileText,
  label: 'Inhoud',
  desc: 'Klopt het sommetje, de tekst, het feit?',
  color: 'fuchsia'
}, {
  n: 5,
  icon: BookOpen,
  label: 'Niveau',
  desc: 'Past de moeilijkheid bij groep 6, 7 of 8?',
  color: 'rose'
}, {
  n: 6,
  icon: Eye,
  label: 'Hulpmiddel',
  desc: 'Werkt het plaatje, de klok, de kaart?',
  color: 'orange'
}, {
  n: 7,
  icon: LifeBuoy,
  label: 'Uitleg',
  desc: 'Krijgt het kind goede uitleg bij een fout?',
  color: 'amber'
}, {
  n: 8,
  icon: ShieldCheck,
  label: 'Eindkeuring',
  desc: 'Mag deze vraag naar buiten?',
  color: 'emerald'
}] as const;
const COLOR_MAP: Record<string, {
  bg: string;
  text: string;
  border: string;
}> = {
  sky: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200'
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-200'
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200'
  },
  fuchsia: {
    bg: 'bg-fuchsia-50',
    text: 'text-fuchsia-700',
    border: 'border-fuchsia-200'
  },
  rose: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200'
  },
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200'
  }
};
export function PipelineSection() {
  const [activeIdx, setActiveIdx] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const headerY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const mascotY = useTransform(scrollYProgress, [0, 1], [80, -80]);
  useEffect(() => {
    const t = setInterval(() => setActiveIdx(i => (i + 1) % (CHECKS.length + 1)), 1100);
    return () => clearInterval(t);
  }, []);
  return <section ref={sectionRef} id="kwaliteit" className="relative py-24 lg:py-32 bg-white overflow-hidden" data-id="element-542">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-543">
        {/* Header */}
        <div className="flex flex-col lg:flex-row gap-10 items-start lg:items-end mb-16" data-id="element-544">
          <motion.div className="flex-1" style={{
          y: headerY
        }} data-id="element-545">
            <motion.div initial={{
            opacity: 0,
            y: 10,
            rotate: 3
          }} whileInView={{
            opacity: 1,
            y: 0,
            rotate: 2
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 bg-rose-100 text-rose-700 border border-rose-200 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6" data-id="element-546">
              <ShieldCheck className="w-3.5 h-3.5" data-id="element-547" />
              Kwaliteitscheck
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight leading-[0.95]" data-id="element-548">
              Geen vraag bereikt je kind zonder{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-orange-500" data-id="element-549">
                8 keer ja.
              </span>
            </h2>
            <p className="text-lg text-slate-600 font-medium max-w-2xl" data-id="element-550">
              Voordat een vraag in beeld komt, gaat hij langs acht checks. Vorm,
              inhoud, niveau, uitleg — als één check op rood gaat, gaat de vraag
              terug. Zo houden we de bar hoog.
            </p>
          </motion.div>

          <motion.div className="shrink-0 hidden lg:block" style={{
          y: mascotY
        }} data-id="element-551">
            <DjayMascot size={110} mood="thinking" data-id="element-552" />
          </motion.div>
        </div>

        {/* 8 checks grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-id="element-553">
          {CHECKS.map((stage, idx) => {
          const Icon = stage.icon;
          const c = COLOR_MAP[stage.color];
          const isDone = idx < activeIdx;
          const isActive = idx === activeIdx;
          return <motion.div key={stage.label} className={`relative bg-white rounded-2xl p-4 border-2 transition-all duration-300 flex flex-col items-center text-center ${isActive ? 'border-slate-900 shadow-[0_8px_0_0_rgb(15,23,42)] -translate-y-2' : isDone ? 'border-slate-200 opacity-50' : 'border-slate-200'}`} data-id="element-554">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 border ${c.bg} ${c.border}`} data-id="element-555">
                  <Icon className={`w-5 h-5 ${c.text}`} strokeWidth={3} data-id="element-556" />
                </div>
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1" data-id="element-557">
                  Check {stage.n}
                </div>
                <div className="text-sm font-black text-slate-800 leading-tight mb-1" data-id="element-558">
                  {stage.label}
                </div>
                <div className="text-[10px] font-medium text-slate-500 leading-tight" data-id="element-559">
                  {stage.desc}
                </div>
              </motion.div>;
        })}
        </div>

        {/* Live counter */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 mx-auto max-w-md bg-slate-900 text-white rounded-2xl p-4 flex items-center justify-center gap-4 shadow-xl" data-id="element-560">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-561" />
          <div className="text-base font-black" data-id="element-562">
            <span className="text-orange-400" data-id="element-563">1.247</span>{' '}
            <span className="text-slate-300 font-medium" data-id="element-564">
              vragen goedgekeurd deze week
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}